import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import CaptionedValue from '@/common/components/CaptionedValue/CaptionedValue'
import useNumberFormatter from '@/common/hooks/useNumberFormatter'
import { getSizeCaptionKey, getThicknessDisplay, hasWebThickness, MATERIAL_PROFILE_TYPES } from '@/common/utils'

const RenderDimensions = ({ orderItemId, selectedPaths }) => {
    const { t } = useTranslation()
    const params = useParams()
    const { orderId } = params
    const organisationId = useSelector(selectOrganisationId)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const drawingCleanerDecimalPlaces = (orderItem) => (orderItem?.isImperial ? 4 : 2)

    const { n } = useNumberFormatter({
        numberOfDecimalPlaces: drawingCleanerDecimalPlaces(orderItem),
    })

    const pathDimensions = Object.values(selectedPaths).length === 1 ? Object.values(selectedPaths)[0] : null

    if (!orderItem || !selectedPaths) return null

    const dimensions = orderItem?.profile
        ? orderItem.profile.toLowerCase() === 'circle'
            ? `${n(orderItem.diameter)} x ${n(orderItem.rotaryPartLength)}`
            : `${n(orderItem.rotaryProfileWidth)} x ${n(orderItem.rotaryProfileLength)} x ${n(
                  orderItem.rotaryPartLength
              )}`
        : `${n(orderItem.displayBoundBoxWidth)} x ${n(orderItem.displayBoundBoxHeight)}`

    // Only an IBeam has a second thickness term (web vs flange) that tells two otherwise identical
    // records apart, so the suffix is scoped to that profile. Not shown for a selected-path
    // measurement, which describes a region of the drawing rather than the part's material.
    const thicknessDisplay =
        orderItem?.profile === MATERIAL_PROFILE_TYPES.IBeam.value ? getThicknessDisplay(orderItem, n) : ''

    // The caption names the order of the part's own size. A selected-path measurement is an
    // arbitrary width x height on the drawing, so it stays uncaptioned.
    const sizeCaption = t(
        getSizeCaptionKey({
            hasLength: Boolean(orderItem.profile),
            isCircle: orderItem.profile?.toLowerCase() === 'circle',
        })
    )
    const caption = pathDimensions
        ? ''
        : hasWebThickness(orderItem)
          ? `${sizeCaption} · ${t('web / flange')}`
          : sizeCaption

    return (
        <CaptionedValue caption={caption}>
            <Typography
                sx={orderItem.profile ? { whiteSpace: 'nowrap' } : undefined}
                variant="body1"
            >
                {pathDimensions
                    ? `${n(pathDimensions.width)} x ${n(pathDimensions.height)}`
                    : thicknessDisplay
                      ? `${dimensions} · ${thicknessDisplay}`
                      : dimensions}
            </Typography>
        </CaptionedValue>
    )
}

RenderDimensions.propTypes = {
    orderItemId: PropTypes.string,
    selectedPaths: PropTypes.object,
}

export default RenderDimensions
